import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBdsLanguage } from '@/core/hooks/useBdsLanguage';
import { getReelsCoverUploadConfig, getReelsUploadConfig } from './uploadConfigs';
import { useUploadHandler } from './useUploadHandler';
import { ValidationResult, validateReelsCaption } from '../../utils/mediaValidation';
import { ValidationFeedback } from '@/core/components/ValidationFeedback/ValidationFeedback';
import { ImageEditButton } from './ImageEditor/ImageEditButton';
import { ImageEditor } from './ImageEditor/ImageEditor';
import './ReelsContent.scss';

interface ReelsContentProps {
  onValidationChange?: (isValid: boolean, file: File | null, validation: ValidationResult | null) => void;
  onCaptionChange?: (caption: string) => void;
  caption?: string;
  coverFile?: File | null;
  onCoverFileChange?: (file: File | null) => void;
  shareToFeed?: boolean;
  onShareToFeedChange?: (shareToFeed: boolean) => void;
  hideMediaUpload?: boolean;
  showReelsOptions?: boolean;
  disabled?: boolean;
}

export const ReelsContent: React.FC<ReelsContentProps> = ({
  onValidationChange,
  onCaptionChange,
  caption,
  coverFile,
  onCoverFileChange,
  shareToFeed = true,
  onShareToFeedChange,
  hideMediaUpload = false,
  showReelsOptions = true,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const bdsLanguage = useBdsLanguage();
  const [reelsCaption, setReelsCaption] = useState<string>('');
  const [captionValidation, setCaptionValidation] = useState<ValidationResult | null>(null);
  const [pendingCoverFile, setPendingCoverFile] = useState<File | null>(null);
  const [coverSourceFile, setCoverSourceFile] = useState<File | null>(null);
  const [hiddenCoverFile, setHiddenCoverFile] = useState<File | null>(null);
  const coverPreviewImageRef = useRef<HTMLImageElement | null>(null);
  const coverPreviewUrlRef = useRef<string | null>(null);
  
  const {
    validationResult,
    feedbackFileName,
    isValidating,
    setupUploadListeners
  } = useUploadHandler({
    publicationType: 'reels',
    onValidationChange
  });

  const uploadConfig = getReelsUploadConfig(t);
  const coverUploadConfig = getReelsCoverUploadConfig(t);
  const updateCoverPreview = useCallback((file: File | null) => {
    if (coverPreviewUrlRef.current) {
      URL.revokeObjectURL(coverPreviewUrlRef.current);
    }

    const previewUrl = file ? URL.createObjectURL(file) : null;
    coverPreviewUrlRef.current = previewUrl;
    if (previewUrl) {
      coverPreviewImageRef.current?.setAttribute('src', previewUrl);
    } else {
      coverPreviewImageRef.current?.removeAttribute('src');
    }
  }, []);

  useEffect(() => {
    if (coverFile) updateCoverPreview(coverFile);
    return () => updateCoverPreview(null);
  }, [coverFile, showReelsOptions, updateCoverPreview]);

  useEffect(() => {
    const coverUpload = document.getElementById('upload-reels-cover');
    if (!coverUpload) return;

    const handleCoverUploadChange = (event: Event) => {
      const detail = (event as CustomEvent<{ files?: FileList; value?: File[] } | File[]>).detail;
      const file = Array.isArray(detail)
        ? detail[0] ?? null
        : detail?.value?.[0] ?? detail?.files?.[0] ?? null;
      setPendingCoverFile(file);
      setCoverSourceFile(file);
    };
    const handleCoverUploadDelete = () => {
      setPendingCoverFile(null);
      setCoverSourceFile(null);
      setHiddenCoverFile(coverFile ?? null);
      onCoverFileChange?.(null);
      updateCoverPreview(null);
    };

    coverUpload.addEventListener('bdsUploadChange', handleCoverUploadChange);
    coverUpload.addEventListener('bdsUploadDelete', handleCoverUploadDelete);
    return () => {
      coverUpload.removeEventListener('bdsUploadChange', handleCoverUploadChange);
      coverUpload.removeEventListener('bdsUploadDelete', handleCoverUploadDelete);
    };
  }, [coverFile, onCoverFileChange, showReelsOptions, updateCoverPreview]);

  const handleCoverConfirm = useCallback((file: File) => {
    onCoverFileChange?.(file);
    setHiddenCoverFile(null);
    updateCoverPreview(file);
    setPendingCoverFile(null);
  }, [onCoverFileChange, updateCoverPreview]);

  useEffect(() => {
    const radioGroup = document.getElementById('reels-share-to-feed-radio');
    if (!radioGroup) return;

    const handleShareToFeedChange = (event: Event) => {
      const value = (event as CustomEvent<{ value?: string }>).detail?.value;
      if (value === 'true') onShareToFeedChange?.(true);
      if (value === 'false') onShareToFeedChange?.(false);
    };

    radioGroup.addEventListener('bdsRadioGroupChange', handleShareToFeedChange);
    return () => radioGroup.removeEventListener('bdsRadioGroupChange', handleShareToFeedChange);
  }, [onShareToFeedChange, showReelsOptions]);

  useEffect(() => {
    const captionInput = document.getElementById('reels-caption-input');
    if (captionInput) {
      const handleCaptionChange = (event: any) => {
        let newValue = '';
        if (typeof event.detail === 'string') {
          newValue = event.detail;
        } else if (event.detail && typeof event.detail.value === 'string') {
          newValue = event.detail.value;
        } else if (event.target && typeof event.target.value === 'string') {
          newValue = event.target.value;
        }
        
        setReelsCaption(newValue);
        
        const validation = validateReelsCaption(newValue, t);
        setCaptionValidation(validation);
        
        onCaptionChange?.(newValue);
      };

      captionInput.addEventListener('bdsInput', handleCaptionChange);
      captionInput.addEventListener('bdsChange', handleCaptionChange);
      captionInput.addEventListener('input', handleCaptionChange);
      captionInput.addEventListener('change', handleCaptionChange);

      return () => {
        captionInput.removeEventListener('bdsInput', handleCaptionChange);
        captionInput.removeEventListener('bdsChange', handleCaptionChange);
        captionInput.removeEventListener('input', handleCaptionChange);
        captionInput.removeEventListener('change', handleCaptionChange);
      };
    }
  }, [onCaptionChange, showReelsOptions, t]);

  useEffect(() => {
    const listeners = setupUploadListeners();
    
    return () => {
      listeners?.cleanup();
    };
  }, [setupUploadListeners]);

  return (
    <>
      {showReelsOptions && <div className="scheduler-reels-options" aria-disabled={disabled}>
      <bds-typo tag="p" variant="fs-16" bold="bold">
        {t('upload.reelsShareToFeedTitle')}
      </bds-typo>
      <bds-radio-group
        id="reels-share-to-feed-radio"
        aria-label={t('upload.reelsShareToFeedTitle')}
        value={shareToFeed ? 'true' : 'false'}
      >
        <bds-grid direction="row" gap="2">
          <bds-radio
            value="true"
            label={t('upload.reelsShareToFeedOption')}
            checked={shareToFeed}
          />
          <bds-radio
            value="false"
            label={t('upload.reelsOnlyOption')}
            checked={!shareToFeed}
          />
        </bds-grid>
      </bds-radio-group>
      <bds-typo tag="p" variant="fs-12">
        {t(shareToFeed ? 'upload.reelsShareToFeedDescription' : 'upload.reelsOnlyDescription')}
      </bds-typo>

      {!hideMediaUpload && <bds-upload
        id="upload-reels"
        accept={uploadConfig.accept}
        multiple={false}
        language={bdsLanguage}
        subtitle={uploadConfig.subtitle}
        title-name={uploadConfig.titleName}
        max-size={uploadConfig.maxSize}
      />}
      
      {!isValidating && (
        <ValidationFeedback 
          validationResult={validationResult}
          fileName={feedbackFileName ?? undefined}
        />
      )}
      
      <bds-upload
        id="upload-reels-cover"
        accept={coverUploadConfig.accept}
        multiple={false}
        language={bdsLanguage}
        subtitle={coverUploadConfig.subtitle}
        title-name={coverUploadConfig.titleName}
        max-size={coverUploadConfig.maxSize}
      />
      {coverFile && coverFile !== hiddenCoverFile && (
        <div className="scheduled-image-editor_preview">
          <img ref={coverPreviewImageRef} alt={t('upload.selectedImagePreviewAlt')} />
          <div className="scheduled-image-editor_preview-actions">
            <ImageEditButton
              label={t('upload.editCoverImage')}
              onEdit={() => setPendingCoverFile(coverSourceFile ?? coverFile)}
              disabled={disabled}
            />
          </div>
        </div>
      )}
      {pendingCoverFile && (
        <ImageEditor
          sourceFile={pendingCoverFile}
          onConfirm={handleCoverConfirm}
          imageContext="reelsCover"
        />
      )}

      <bds-input
        id="reels-caption-input"
        label={t('caption')}
        placeholder={t('reelsCaptionPlaceholder')}
        helper-text={`${(caption ?? reelsCaption).length}/2200 ${t('characters')}`}
        counter-length={true}
        maxlength={2200}
        is-textarea={true}
        value={caption ?? reelsCaption}
        maxlength-error-message={t('maxCaptionError')}
        disabled={disabled}
      />
      
      {captionValidation && !captionValidation.isValid && (
        <ValidationFeedback 
          validationResult={captionValidation}
        />
      )}
      </div>}
    </>
  );
};